// Global crisis-band overlay — the mirror of baselines.jsx, but for shaded
// historical-crisis windows instead of benchmark curves. The user picks which
// crises to annotate once (top-bar menu); the choice persists (localStorage)
// and EVERY equity / drawdown plot shades them. Default: ALL crises selected,
// so the bands are annotated everywhere out of the box. Fed by
// /api/strategy/crises — the 9 canonical SPY peak→trough windows (dot-com …
// 2025 tariff selloff), one source of truth shared with the backtest stats.
import { createContext, useContext, useEffect, useState } from "react";
import { yearFrac } from "./dates.js";
import { api } from "./api.js";

const Ctx = createContext({ windows: [], selected: [], toggle: () => {}, setAll: () => {} });
const KEY = "cc-crises";

// date string -> fractional year (matches every chart's own yearFrac helper).
// the SSOT yearFrac (dates.js) — a local copy here once drifted from the chart formula (G-10)
const yf = yearFrac;

export function CrisisProvider({ children }) {
  const [windows, setWindows] = useState([]);
  // null = "user hasn't chosen yet" -> default to ALL once windows load. After
  // any toggle we persist an explicit array (which may be empty = none).
  const [selected, setSelected] = useState(() => {
    try { const v = localStorage.getItem(KEY); return v == null ? null : JSON.parse(v); } catch { return null; }
  });
  useEffect(() => {
    api("strategy/crises").then((d) => {
      const w = (d.windows || []).filter((x) => x.start && x.end);
      setWindows(w);
      setSelected((s) => (s == null ? w.map((x) => x.key) : s));   // default: all on
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (selected != null) { try { localStorage.setItem(KEY, JSON.stringify(selected)); } catch {} }
  }, [selected]);
  const sel = selected || [];
  const toggle = (k) => setSelected((s) => {
    const cur = s || []; return cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
  });
  const setAll = (on) => setSelected(on ? windows.map((w) => w.key) : []);
  return <Ctx.Provider value={{ windows, selected: sel, toggle, setAll }}>{children}</Ctx.Provider>;
}
export const useCrises = () => useContext(Ctx);

// Reusable SVG band layer for any chart. Maps each selected crisis window onto
// the chart's own x-scale (mx), clipped to its visible domain [t0,t1], and drops
// windows fully outside the view. `labels` shows the crisis name on bands wide
// enough not to collide; every band carries an SVG <title> for hover. Render it
// as the FIRST child of the <svg> so bands sit behind grid lines and curves.
// `vlabels` renders EVERY crisis name as a vertical (rotated) label at the band's
// left edge — so narrow/clustered windows are still named (the wide-only horizontal
// `labels` mode collides or drops names). Reading top→down keeps the footprint ~9px.
// `noFill` draws the window as faint start/end edge lines instead of a filled band
// (so it doesn't muddy a coloured fill underneath, e.g. the red drawdown area).
// `vlabelBottom` renders the vertical name at the bottom edge reading up (for charts
// whose curve hugs the top, like drawdown).
export function CrisisBands({ mx, t0, t1, y = 0, h, labels = false, vlabels = false, noFill = false, vlabelBottom = false }) {
  const { windows, selected } = useCrises();
  const on = new Set(selected);
  const bands = windows
    .filter((w) => on.has(w.key))
    .map((w) => ({ key: w.key, name: w.name, a: Math.max(t0, yf(w.start)), b: Math.min(t1, yf(w.end)) }))
    .filter((c) => c.b > c.a);
  return bands.map((c) => {
    const x = mx(c.a), w = Math.max(1.5, mx(c.b) - mx(c.a));
    // top labels read down from (x+3.6, y+5); bottom labels read up from near (x+3.6, y+h-5)
    const lx = (x + 3.6).toFixed(1);
    const ly = (vlabelBottom ? y + h - 5 : y + 5).toFixed(1);
    const rot = vlabelBottom ? -90 : 90;
    return (
      <g key={c.key}>
        {noFill ? (
          <>
            <line x1={x.toFixed(1)} y1={y} x2={x.toFixed(1)} y2={y + h} stroke="var(--band-solid)" strokeWidth="1" opacity="0.5" vectorEffect="non-scaling-stroke" />
            <line x1={(x + w).toFixed(1)} y1={y} x2={(x + w).toFixed(1)} y2={y + h} stroke="var(--band-solid)" strokeWidth="1" opacity="0.5" vectorEffect="non-scaling-stroke" />
          </>
        ) : (
          <rect x={x.toFixed(1)} y={y} width={w.toFixed(1)} height={h} fill="var(--band)"><title>{c.name}</title></rect>
        )}
        {labels && w > 34 && (
          <text x={(x + 4).toFixed(1)} y={y + 14} fontSize="10" fill="var(--text-3)" fontFamily="var(--font-mono)">{c.name}</text>
        )}
        {vlabels && (
          <text x={lx} y={ly} fontSize="9" fill="var(--text-2)" fontFamily="var(--font-mono)"
            opacity="0.9" transform={`rotate(${rot} ${lx} ${ly})`} style={{ pointerEvents: "none" }}>{c.name}</text>
        )}
      </g>
    );
  });
}
